import {
  Actions,
  ArrowRightIcon,
  BankCardDoubleIcon,
  FingerprintScanIcon,
  Typography,
} from "@cpj-ai/panel-npm-package";
import { AuthAccountOrganizationStatusChoices, useGetOrganizationInfoQuery } from "@graphql/generated";
import { useOrganizationStore } from "@store/useOrganizationStore";
import { useMemo, useState } from "react";

import { routes } from "@constants/routes";
import { Link } from "react-router-dom";
import JobPositionTermsAndConditionConfirm from "./JobPositionTermsAndConditionConfirm";

interface IRegulationsStepContentProps {
  handleNextStep: () => void;
}

interface StatusItem {
  label: string;
  value: string;
  description: string;
  Icon: React.ComponentType<any>;
  status: string | boolean | null | undefined;
  statusConfig: { label: string; color: string; textColor: string } | null;
}

interface StatusConfig {
  label: string;
  color: string;
  textColor: string;
}

export const ORGANIZATION_STATUS: Record<AuthAccountOrganizationStatusChoices, StatusConfig> = {
  DRAFTED: {
    label: "Drafted",
    color: "bg-gray-500",
    textColor: "text-white",
  },
  SUBMITTED: {
    label: "Submitted",
    color: "bg-blue-500",
    textColor: "text-white",
  },
  REJECTED: {
    label: "Rejected",
    color: "bg-red-500",
    textColor: "text-white",
  },
  VERIFIED: {
    label: "Verified",
    color: "bg-green-500",
    textColor: "text-white",
  },
  BLOCKED: {
    label: "Blocked",
    color: "bg-red-500",
    textColor: "text-white",
  },
  COMPLETED: {
    label: "Completed",
    color: "bg-yellow-500",
    textColor: "text-white",
  },
};

const FINANCIAL_STATUS: Record<string, StatusConfig> = {
  true: {
    label: "Verified",
    color: "bg-green-500",
    textColor: "text-white",
  },
  false: {
    label: "Not Verified",
    color: "bg-red-500",
    textColor: "text-white",
  },
};

const RegulationsStepContent = ({ handleNextStep }: IRegulationsStepContentProps) => {
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const { data } = useGetOrganizationInfoQuery();
  const { selectedOrganization } = useOrganizationStore();

  const organizationData = useMemo(() => {
    return data?.me?.organizationMemberships.find(
      (organization) => organization.organization?.id === selectedOrganization?.id,
    )?.organization;
  }, [data?.me?.organizationMemberships, selectedOrganization]);

  const status = useMemo(() => {
    return [
      {
        label: "Company authentication status",
        value: "1",
        description:
          "Before registering an ad, your company must be authenticated in order to be allowed to register an ad",
        Icon: FingerprintScanIcon,
        status: organizationData?.status,
        statusConfig: organizationData?.status ? ORGANIZATION_STATUS[organizationData.status] : null,
      },
      {
        label: "Financial information",
        value: "2",
        description: "To register a new job post, your financial information needs to be registered and confirmed",
        Icon: BankCardDoubleIcon,
        status: organizationData?.hasFinancialData,
        statusConfig:
          organizationData?.hasFinancialData !== undefined && organizationData.hasFinancialData !== null
            ? FINANCIAL_STATUS[organizationData.hasFinancialData.toString()]
            : null,
      },
    ] as StatusItem[];
  }, [organizationData]);

  const isVerificationComplete = useMemo(() => {
    const isOrganizationVerified = organizationData?.status === AuthAccountOrganizationStatusChoices.VERIFIED;
    const isFinancialVerified = organizationData?.hasFinancialData === true;

    return isOrganizationVerified && isFinancialVerified;
  }, [organizationData]);

  const renderStatus = (item: StatusItem) => {
    if (item.status === undefined || item.status === null) {
      return (
        <Typography variant="small" className="bg-yellow-500 text-white rounded-lg p-1 px-2">
          Checking your verify ...
        </Typography>
      );
    }

    return (
      <Typography
        variant="small"
        className={`rounded-lg p-1 px-2 ${item.statusConfig?.color} ${item.statusConfig?.textColor}`}
      >
        {item.statusConfig?.label}
      </Typography>
    );
  };

  const renderVerificationMessage = () => {
    if (isVerificationComplete) {
      return null;
    }

    if (!organizationData?.status) {
      return (
        <Typography variant="small" className="text-yellow-500 mt-4">
          Loading organization verification status...
        </Typography>
      );
    }

    const orgStatus = organizationData.status;
    const hasFinancialData = organizationData.hasFinancialData;

    if (
      [AuthAccountOrganizationStatusChoices.BLOCKED, AuthAccountOrganizationStatusChoices.REJECTED].includes(orgStatus)
    ) {
      return (
        <Typography variant="small" className="text-red-500 mt-4">
          Your organization is {orgStatus.toLowerCase()}. Please contact support for assistance.
        </Typography>
      );
    }

    return (
      <Typography variant="small" className="text-red-500 mt-4">
        {orgStatus === AuthAccountOrganizationStatusChoices.DRAFTED && (
          <>
            Your organization profile is incomplete. Please complete your organization profile to proceed.
            <Link to={`${routes.profile.main}`} className="text-blue-500 ms-2">
              Complete Organization Profile
            </Link>
          </>
        )}
        {orgStatus === AuthAccountOrganizationStatusChoices.SUBMITTED && (
          <>
            Your organization profile is under review. Please wait for verification to complete or contact support if
            you have questions.
          </>
        )}
        {orgStatus === AuthAccountOrganizationStatusChoices.COMPLETED && !hasFinancialData && (
          <>
            Please complete your financial information to proceed with job posting.
            <Link to={`${routes.profile.main}?tab=financial`} className="text-blue-500 ms-2">
              Complete Financial Information
            </Link>
          </>
        )}
        {orgStatus === AuthAccountOrganizationStatusChoices.COMPLETED && hasFinancialData && (
          <>
            Your organization profile is complete but pending verification. Please ensure all information is accurate.
            <Link to={`${routes.profile.main}?tab=verify`} className="text-blue-500 ms-2">
              View Organization Status
            </Link>
          </>
        )}
      </Typography>
    );
  };

  return (
    <div className="pb-11">
      <div className="flex flex-col gap-4">
        {status.map((item) => (
          <div
            key={item.label}
            className="w-full flex flex-col md:flex-row gap-4 p-4 items-center justify-between max-w-full overflow-hidden rounded-lg border border-default-200 m-0"
          >
            <div className="flex items-start">
              <item.Icon className="me-2 w-9 h-9" stroke="#000" viewBox="0 0 36 36" />
              <div className="flex flex-col  w-full">
                <div className="flex items-center gap-2 mb-2">
                  <Typography className="font-bold">{item.label}</Typography>
                </div>
                <Typography variant="small">{item.description}</Typography>
              </div>
            </div>
            {renderStatus(item)}
          </div>
        ))}
      </div>
      {renderVerificationMessage()}
      <JobPositionTermsAndConditionConfirm acceptedTerms={acceptedTerms} setAcceptedTerms={setAcceptedTerms} />
      <Actions
        className="mt-6"
        showCancel={false}
        submitProps={{
          onClick: handleNextStep,
          isDisabled: !acceptedTerms || !isVerificationComplete,
          children: "Next",
          endContent: <ArrowRightIcon stroke="#fff" className="w-4 h-4" />,
        }}
      />
    </div>
  );
};

export default RegulationsStepContent;
